<template>
    <div class="technologies">
        <div class="heading">
            <h1 class="title">State of Technology</h1>
            <p class="description">
                The Entelect state of technology report is generated by a companywide survey focused on technology and
                how we work. The goal is to uncover trends in the work we do, the technology we use, and the processes
                involved in delivering world-class solutions.
            </p>
        </div>

        <div class="tabs">
            <div class="tab">
                <router-link class="icon-button" to="java" active-class="active" append>
                    <img :src="iconImages.java"/><span class="extra-description">Java</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="dotnet" active-class="active" append>
                    <img :src="iconImages.cSharp"/><span class="extra-description">C# (.Net)</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="web" active-class="active" append>
                    <img :src="iconImages.web"/><span class="extra-description">Web</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="mobile" active-class="active" append>
                    <img :src="iconImages.mobile"/><span class="extra-description">Mobile</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="data" active-class="active" append>
                    <img :src="iconImages.data"/><span class="extra-description">Data</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="analysis" active-class="active" append>
                    <img :src="iconImages.analysis"/><span class="extra-description">Analysis</span>
                </router-link>
            </div>
            <div class="tab">
                <router-link class="icon-button" to="fun" active-class="active" append>
                    <img :src="iconImages.funStuff"/><span class="extra-description">Fun stuff</span>
                </router-link>
            </div>
        </div>
        <div class="state-of-technology">
            <div class="sub-title">
                <h3>Programming languages currently used</h3>
                <p>
                    The programming languages that Entelectuals use the most
                </p>
            </div>

            <div class="languages-used-chart">
                <apex-charts type="bar" :options="languagesUsedDataSource.chartOptions" height="320"
                             :series="languagesUsedDataSource.series"></apex-charts>
            </div>
            <div class="languages-used-vs-interest-chart">
                <apex-charts type="heatmap" :options="languagesUsedVSInterestDataSource.chartOptions" height="200"
                             :series="languagesUsedVSInterestDataSource.series"></apex-charts>
            </div>

            <div class="tool-usage-continued">
                <div class="ide">
                    <apex-charts type="radialBar" :options="ideUsageDataSource.chartOptions"
                                 :series="ideUsageDataSource.series"></apex-charts>
                </div>
                <div class="issue-tracker">
                    <apex-charts type="radialBar" :options="issueTrackerUsageDataSource.chartOptions"
                                 :series="issueTrackerUsageDataSource.series"></apex-charts>
                </div>
                <div class="text-editors">
                    <apex-charts type="heatmap" height="225" :options="textEditorUsageDataSource.chartOptions"
                                 :series="textEditorUsageDataSource.series"></apex-charts>
                </div>
                <div class="source-control">
                    <apex-charts type="bar" height="175" :options="sourceControlUsageDataSource.chartOptions"
                                 :series="sourceControlUsageDataSource.series"></apex-charts>
                </div>
                <div class="shell">
                    <apex-charts type="bar" height="175"
                                 :options="operatingSystemUsageDataSource.chartOptions"
                                 :series="operatingSystemUsageDataSource.series"></apex-charts>
                </div>

                <div class="areas-of-work">
                    <apex-charts type="bar" height="300" :options="areasOfWorkDataSource.chartOptions"
                                 :series="areasOfWorkDataSource.series"></apex-charts>
                </div>
                <div class="databases">
                    <apex-charts type="bar" height="300" :options="databasesUsageDataSource.chartOptions"
                                 :series="databasesUsageDataSource.series"></apex-charts>
                </div>

                <div class="fun-facts">
                    <div class="fun-fact">
                        58% of developers spend between 30 and 40 hours a week coding
                    </div>
                    <div class="fun-fact">
                        28% of applications run in containers in production
                    </div>
                    <div class="fun-fact">
                        56% of applications run on Windows in production whilst 41% run on Unix
                    </div>
                </div>

                <div class="deployment-control">
                    <apex-charts type="bar" height="175"
                                 :options="deploymentControlUsageDataSource.chartOptions"
                                 :series="deploymentControlUsageDataSource.series"></apex-charts>
                </div>
                <div class="continuous-integration">
                    <apex-charts type="donut" :options="continuousIntegrationUsageDataSource.chartOptions"
                                 :series="continuousIntegrationUsageDataSource.series"></apex-charts>
                </div>
                <div class="hosting-platform">
                    <apex-charts type="bar" height="175" :options="hostingPlatformDataSource.chartOptions"
                                 :series="hostingPlatformDataSource.series"></apex-charts>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import VueApexCharts from "vue-apexcharts/src";
    // Icon Images
    import javaImage from '../../assets/images/Java_Cup.svg'
    import cSharpImage from '../../assets/images/C_Sharp.svg'
    import webImage from '../../assets/images/Web.svg'
    import mobileImage from '../../assets/images/Mobile.svg'
    import dataImage from '../../assets/images/Data.svg'
    import analysisImage from '../../assets/images/Analysis.svg'
    import funStuffImage from '../../assets/images/FunStuff.svg'

    export default {
        components: {
            'apex-charts': VueApexCharts,
        },
        data: function () {
            return {
                iconImages: {
                    java: javaImage,
                    cSharp: cSharpImage,
                    web: webImage,
                    mobile: mobileImage,
                    data: dataImage,
                    analysis: analysisImage,
                    funStuff: funStuffImage,
                },
                languagesUsedDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            },
                            width: '100%'
                        },
                        colors: ['#008AD0', '#F7941D', '#B51E8E', '#009EA5', '#00994C'],
                        plotOptions: {
                            bar: {
                                dataLabels: {
                                    position: 'top', // top, center, bottom
                                },
                            }
                        },
                        legend: {
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    color: '#FFF',
                                },
                            }
                        },
                        xaxis: {
                            categories: [
                                'SQL', 'JavaScript', 'Java', 'C#', 'Typescript', 'Python', 'Swift', 'Other'
                            ],
                            labels: {
                                style: {
                                    colors: '#FFF',
                                },
                            }
                        },
                        dataLabels: {
                            offsetY: -20,
                            formatter: (value) => `${Math.round(value)}%`
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [{
                        name: 'Usage',
                        data: [50.6, 50, 46.4, 44.4, 37, 4.7, 3.6, 16.2]
                    }]
                },
                languagesUsedVSInterestDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            },
                            width: '100%'
                        },
                        title: {
                            text: 'Language popularity',
                            style: {
                                color: '#FFF'
                            }
                        },
                        plotOptions: {
                            heatmap: {
                                colorScale: {
                                    ranges: [
                                        {
                                            from: 0,
                                            to: 5,
                                            name: 'low usage',
                                            color: '#008AD0'
                                        },
                                        {
                                            from: 6,
                                            to: 20,
                                            name: 'medium',
                                            color: '#00994C'
                                        },
                                        {
                                            from: 21,
                                            to: 160,
                                            name: 'high',
                                            color: '#F7941D'
                                        },
                                        {
                                            from: 161,
                                            to: 300,
                                            name: 'extreme usage',
                                            color: '#B51E8E'
                                        }
                                    ],
                                }
                            },
                        },
                        legend: {
                            offsetY: 24,
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    color: '#FFF',
                                },
                            }
                        },
                        xaxis: {
                            labels: {
                                rotateAlways: true,
                                style: {
                                    colors: '#FFF',
                                },
                            }
                        },
                        dataLabels: {
                            enabled: false
                        },
                        tooltip: {
                            theme: 'dark'
                        },
                        responsive: [
                            {
                                breakpoint: 960,
                                options: {
                                    xaxis: {
                                        labels: {
                                            show: false
                                        }
                                    }
                                }
                            }
                        ]
                    },
                    series: [{
                        name: '2017',
                        data: [
                            {x: 'C#', y: 135},
                            {x: 'C/C++', y: 3},
                            {x: 'Go', y: 1},
                            {x: 'Groovy', y: 1},
                            {x: 'Java', y: 156},
                            {x: 'JavaScript', y: 208},
                            {x: 'Kotlin', y: 5},
                            {x: 'Objective-C', y: 5},
                            {x: 'Power BI', y: 2},
                            {x: 'Python', y: 8},
                            {x: 'Qlik', y: 3},
                            {x: 'R', y: 4},
                            {x: 'Ruby', y: 3},
                            {x: 'Scala', y: 8},
                            {x: 'SQL', y: 193},
                            {x: 'Swift', y: 9},
                            {x: 'TypeScript', y: 17},
                        ]
                    }, {
                        name: '2018',
                        data: [
                            {x: 'C#', y: 151},
                            {x: 'C/C++', y: 1},
                            {x: 'Go', y: 4},
                            {x: 'Groovy', y: 3},
                            {x: 'Java', y: 157},
                            {x: 'JavaScript', y: 169},
                            {x: 'Kotlin', y: 7},
                            {x: 'Objective-C', y: 4},
                            {x: 'Power BI', y: 3},
                            {x: 'Python', y: 16},
                            {x: 'Qlik', y: 1},
                            {x: 'R', y: 4},
                            {x: 'Ruby', y: 3},
                            {x: 'Scala', y: 10},
                            {x: 'SQL', y: 171},
                            {x: 'Swift', y: 12},
                            {x: 'TypeScript', y: 129},]
                    }]
                },
                ideUsageDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            }
                        },
                        colors: ['#F7941D', '#009EA5', '#008AD0', '#B51E8E', '#00994C'],
                        labels: ['Intellij', 'Visual Studio', 'VS Code', 'SQL Studio', 'Android Studio'],
                        title: {
                            text: 'IDEs',
                            style: {
                                color: '#FFF'
                            }
                        },
                        stroke: {
                            width: 0
                        },
                        fill: {
                            patern: {
                                width: 500
                            }
                        },
                        tooltip: {
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                        dataLabels: {
                            name: {
                                show: false,

                            },
                            value: {
                                show: false,
                            },
                            formatter: function (val, opt) {
                                if (val < 5)
                                    return '';
                                return `${opt.w.globals.labels[opt.seriesIndex]}: ${Math.round(val)}%`;
                            },
                        },
                        plotOptions: {
                            radialBar: {
                                offsetY: -30,
                                startAngle: 0,
                                endAngle: 270,
                                hollow: {
                                    margin: 5,
                                    size: '15%',
                                    background: 'transparent',
                                    image: undefined,
                                },
                                track: {
                                    opacity: 0.1
                                },
                                dataLabels: {
                                    name: {
                                        show: false,
                                    },
                                    value: {
                                        color: '#fff',
                                        offsetY: 4
                                    }
                                }
                            }
                        },
                        legend: {
                            show: true,
                            floating: true,
                            fontSize: '14px',
                            position: 'left',
                            verticalAlign: 'top',
                            textAnchor: 'end',
                            labels: {
                                useSeriesColors: true,
                            },
                            markers: {
                                size: 0
                            },
                            itemMargin: {
                                vertical: 5,
                            },
                            containerMargin: {
                                left: 140,
                                top: 26
                            }
                        },
                        responsive: [{
                            breakpoint: 599,
                            options: {
                                legend: {
                                    show: true,
                                    fontSize: '16px',
                                    position: 'bottom',
                                    textAnchor: 'start',
                                    labels: {
                                        useSeriesColors: false,
                                        color: '#fff'
                                    },
                                    markers: {
                                        size: 5
                                    },
                                    containerMargin: {
                                        left: 0,
                                        top: 0
                                    }
                                },
                                plotOptions: {
                                    radialBar: {
                                        offsetY: 0,
                                        startAngle: 270,
                                        endAngle: 90,
                                    }
                                },
                            }
                        }],
                    },
                    series: [49.7, 45.6, 36.4, 38.8, 10.7]
                },
                issueTrackerUsageDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            }
                        },
                        colors: ['#F7941D', '#009EA5', '#008AD0', '#B51E8E'],
                        labels: ['JIRA', 'Trello', 'Physical board', 'YouTrack'],
                        title: {
                            text: 'Issue trackers',
                            style: {
                                color: '#FFF'
                            }
                        },
                        stroke: {
                            width: 0
                        },
                        fill: {
                            patern: {
                                width: 500
                            }
                        },
                        tooltip: {
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                        dataLabels: {
                            name: {
                                show: false,

                            },
                            value: {
                                show: false,
                            },
                            formatter: function (val, opt) {
                                if (val < 5)
                                    return '';
                                return `${opt.w.globals.labels[opt.seriesIndex]}: ${Math.round(val)}%`;
                            },
                        },
                        plotOptions: {
                            radialBar: {
                                offsetY: -30,
                                startAngle: 0,
                                endAngle: 270,
                                hollow: {
                                    margin: 5,
                                    size: '25%',
                                    background: 'transparent',
                                    image: undefined,
                                },
                                track: {
                                    opacity: 0.1
                                },
                                dataLabels: {
                                    name: {
                                        show: false,
                                    },
                                    value: {
                                        color: '#fff',
                                        offsetY: 4
                                    }
                                }
                            }
                        },
                        legend: {
                            show: true,
                            floating: true,
                            fontSize: '14px',
                            position: 'left',
                            verticalAlign: 'top',
                            textAnchor: 'end',
                            labels: {
                                useSeriesColors: true,
                            },
                            markers: {
                                size: 0
                            },
                            itemMargin: {
                                vertical: 7,
                            },
                            containerMargin: {
                                left: 140,
                                top: 26
                            }
                        },
                        responsive: [{
                            breakpoint: 599,
                            options: {
                                legend: {
                                    show: true,
                                    fontSize: '16px',
                                    position: 'bottom',
                                    textAnchor: 'start',
                                    labels: {
                                        useSeriesColors: false,
                                        color: '#fff'
                                    },
                                    markers: {
                                        size: 5
                                    },
                                    containerMargin: {
                                        left: 0,
                                        top: 0
                                    }
                                },
                                plotOptions: {
                                    radialBar: {
                                        offsetY: 0,
                                        startAngle: 270,
                                        endAngle: 90,
                                    }
                                },
                            }
                        }],
                    },
                    series: [72.2, 17.5, 13.9, 11.8]
                },
                textEditorUsageDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            },
                            width: '100%'
                        },
                        title: {
                            text: 'Text editor popularity',
                            style: {
                                color: '#FFF'
                            }
                        },
                        colors: ['#008AD0', '#008AD0', '#B51E8E', '#009EA5', '#00994C'],
                        plotOptions: {
                            heatmap: {
                                colorScale: {
                                    ranges: [{
                                        from: 0,
                                        to: 5,
                                        name: 'low usage',
                                        color: '#008AD0'
                                    },
                                        {
                                            from: 6,
                                            to: 20,
                                            name: 'medium',
                                            color: '#00994C'
                                        },
                                        {
                                            from: 21,
                                            to: 40,
                                            name: 'high',
                                            color: '#F7941D'
                                        },
                                        {
                                            from: 41,
                                            to: 300,
                                            name: 'extreme usage',
                                            color: '#B51E8E'
                                        }],
                                }
                            },
                        },
                        legend: {
                            offsetY: 24,
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    color: '#FFF',
                                },
                            }
                        },
                        xaxis: {
                            labels: {
                                rotateAlways: true,
                                style: {
                                    colors: '#FFF',
                                },
                            }
                        },
                        dataLabels: {
                            enabled: false
                        },
                        tooltip: {
                            theme: 'dark'
                        },
                        responsive: [
                            {
                                breakpoint: 960,
                                options: {
                                    xaxis: {
                                        labels: {
                                            show: false
                                        }
                                    }
                                }
                            }
                        ]
                    },
                    series: [{
                        name: '2017',
                        data: [
                            {x: 'Notepad++', y: 170},
                            {x: 'VS Code', y: 89},
                            {x: 'Sublime', y: 26},
                            {x: 'Vi/Vim', y: 9},
                            {x: 'Atom', y: 20},
                            {x: 'Standard Notepad', y: 5},
                            {x: 'Emacs', y: 2},
                            {x: 'GEdit', y: 2},
                            {x: 'Nano', y: 1},
                            {x: 'None', y: 0},
                            {x: 'Boostnote', y: 0},
                            {x: 'Brackets', y: 1},
                            {x: 'Code writer', y: 0},
                            {x: 'Kate', y: 0},
                            {x: 'SciTe', y: 1},
                            {x: 'TextEdit', y: 2},
                            {x: 'Em Editor', y: 1},
                            {x: 'Notepadqq', y: 1}
                        ]
                    }, {
                        name: '2018',
                        data: [
                            {x: 'Notepad++', y: 221},
                            {x: 'VS Code', y: 137},
                            {x: 'Sublime', y: 53},
                            {x: 'Vi/Vim', y: 33},
                            {x: 'Atom', y: 22},
                            {x: 'Standard Notepad', y: 22},
                            {x: 'Emacs', y: 3},
                            {x: 'GEdit', y: 4},
                            {x: 'Nano', y: 3},
                            {x: 'None', y: 2},
                            {x: 'Boostnote', y: 1},
                            {x: 'Brackets', y: 1},
                            {x: 'Code writer', y: 1},
                            {x: 'Kate', y: 1},
                            {x: 'SciTe', y: 1},
                            {x: 'TextEdit', y: 1},
                            {x: 'Em Editor', y: 0},
                            {x: 'Notepadqq', y: 0}
                        ]
                    }]
                },
                sourceControlUsageDataSource: {
                    chartOptions: {
                        chart: {
                            stacked: true,
                            stackType: '100%',
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Source control',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 10) {
                                    return '';
                                }

                                return `${val}%`;
                            }
                        },
                        colors: ['#00994C', '#B51E8E', '#003974', '#F7941D'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                            }
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            show: false,
                            labels: {
                                show: false,
                                style: {
                                    color: '#FFF',
                                },
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    colors: '#FFF',
                                },
                            },
                            categories: ['Source control'],
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [{
                        name: 'Git',
                        data: [87.9]
                    }, {
                        name: 'SVN',
                        data: [3.3]
                    }, {
                        name: 'TFS',
                        data: [2.7]
                    }, {
                        name: 'Other',
                        data: [6.1]
                    }]
                },
                operatingSystemUsageDataSource: {
                    chartOptions: {
                        chart: {
                            stacked: true,
                            stackType: '100%',
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Operating system',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 10) {
                                    return '';
                                }

                                return `${Math.round(val)}%`;
                            }
                        },
                        colors: ['#F7941D', '#003974', '#00994C'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                            }
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            show: false,
                            labels: {
                                show: false,
                                style: {
                                    color: '#FFF',
                                },
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    colors: '#FFF',
                                },
                            },
                            categories: ['Operating system'],
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [{
                        name: 'Windows',
                        data: [81.7]
                    }, {
                        name: 'Linux/Unix',
                        data: [21]
                    }, {
                        name: 'Mac',
                        data: [10.9]
                    }]
                },
                areasOfWorkDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Areas of work',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        colors: ['#008AD0', '#F7941D', '#B51E8E', '#009EA5', '#00994C'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                                distributed: true,
                                dataLabels: {
                                    position: 'bottom',
                                },
                            }
                        },
                        legend: {
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: ['#FFF', '#FFF', '#FFF', '#FFF', '#FFF', '#FFF', '#FFF'],
                                },
                            }
                        },
                        xaxis: {
                            categories: [
                                'APIs / Web back-ends', 'Web front-ends', 'Enterprise back-ends', 'Mobile apps', 'Data',
                                'Desktop apps', 'Embedded'
                            ],
                            labels: {
                                style: {
                                    colors: ['#FFF'],
                                },
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 10) {
                                    return '';
                                }

                                return `${Math.round(val)}%`;
                            }
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        }
                    },
                    series: [{
                        name: 'Area of work',
                        data: [82.5, 71.3, 39.3, 13.9, 9.8, 3.8, 1.8]
                    }]
                },
                databasesUsageDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Databases',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        colors: ['#008AD0', '#F7941D', '#B51E8E', '#009EA5', '#00994C'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                                distributed: true,
                                dataLabels: {
                                    position: 'bottom',
                                },
                            }
                        },
                        legend: {
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: ['#FFF', '#FFF', '#FFF', '#FFF', '#FFF', '#FFF', '#FFF'],
                                },
                            },
                        },
                        xaxis: {
                            categories: [
                                'SQL Server', 'Oracle', 'MongoDB', 'MySQL', 'Postgress', 'Cosmos DB'
                            ],
                            labels: {
                                style: {
                                    colors: ['#FFF'],
                                },
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 10) {
                                    return '';
                                }

                                return `${Math.round(val)}%`;
                            }
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        }
                    },
                    series: [{
                        name: 'Database usage',
                        data: [62.8, 30.3, 14.8, 12.9, 11.4, 6.6]
                    }]
                },
                deploymentControlUsageDataSource: {
                    chartOptions: {
                        chart: {
                            stacked: true,
                            stackType: '100%',
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Control over deployments',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 15) {
                                    return '';
                                }

                                return `${val}%`;
                            }
                        },
                        colors: ['#003974', '#F7941D'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                            }
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            show: false,
                            labels: {
                                show: false,
                                style: {
                                    color: '#FFF',
                                },
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    colors: '#FFF',
                                },
                            },
                            categories: ['Control over deployments'],
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [{
                        name: 'Yes',
                        data: [85]
                    }, {
                        name: 'No',
                        data: [15]
                    }]
                },
                continuousIntegrationUsageDataSource: {
                    chartOptions: {
                        chart: {
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Continuous integration',
                            style: {
                                color: '#FFF'
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 5) {
                                    return '';
                                }

                                return `${Math.round(val)}%`;
                            }
                        },
                        colors: ['#B51E8E', '#009EA5', '#003974', '#F7941D', '#00994C', '#008AD0'],
                        labels: ['Bamboo', 'Jenkins', 'Team city', 'None', 'Azure pipelines', 'Other'],
                        legend: {
                            labels: {
                                color: '#FFF'
                            },
                            position: 'bottom',
                        },
                        stroke: {
                            width: 0
                        },
                        tooltip: {
                            enabled: true,
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [25.1, 24.6, 16.9, 10.7, 7.1, 15.6]
                },
                hostingPlatformDataSource: {
                    chartOptions: {
                        chart: {
                            stacked: true,
                            stackType: '100%',
                            toolbar: {
                                show: false
                            }
                        },
                        title: {
                            text: 'Hosting platform',
                            floating: true,
                            style: {
                                color: '#FFF'
                            }
                        },
                        dataLabels: {
                            formatter: (val) => {
                                if (val < 15) {
                                    return '';
                                }

                                return `${Math.round(val)}%`;
                            }
                        },
                        colors: ['#00994C', '#B51E8E'],
                        plotOptions: {
                            bar: {
                                horizontal: true,
                            }
                        },
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: '#FFF'
                            }
                        },
                        yaxis: {
                            show: false,
                            labels: {
                                show: false,
                                style: {
                                    color: '#FFF',
                                },
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    colors: '#FFF',
                                },
                            },
                            categories: ['Hosting platform'],
                        },
                        tooltip: {
                            theme: 'dark',
                            y: {
                                formatter: (value) => `${value}%`
                            }
                        },
                    },
                    series: [{
                        name: 'On-premises',
                        data: [54.1]
                    }, {
                        name: 'Cloud',
                        data: [45.9]
                    }]
                },
            }
        },
        methods: {
            track() {
                this.$ga.page('/2018/technology/overview') // This is for Google Analytics tracking
            }
        }
    }
</script>

<style lang="scss" scoped>

    .technologies {

        .heading {
            display: grid;
            grid-template-columns: 1fr 2fr 1fr;

            @media only screen and (max-width: $screen-sm-max) {
                grid-template-columns: 1fr 8fr 1fr;
            }

            .title {
                align-self: center;
                justify-self: center;
                margin: 24px 0;
                display: grid;
                align-content: center;
                justify-content: center;
            }

            .description {
                margin-bottom: 16px;
                text-align: justify;
            }

            .title,
            .description {
                grid-column: 2;
                color: #FFF;
            }

        }

        .tabs {
            display: grid;
            grid-auto-columns: 1fr;
            grid-auto-flow: column;
            padding: 24px 0;

            @media only screen and (max-width: $screen-sm-max) {
                grid-auto-columns: initial;
                grid-auto-flow: row;
                grid-template-columns: 1fr 1fr 1fr;
                grid-template-rows: auto auto auto;
            }

            @media only screen and (max-width: $screen-xs-max) {
                grid-template-columns: 1fr;
                grid-template-rows: auto auto auto auto auto auto auto;
            }

            .tab {
                display: grid;
                grid-column: auto;
                align-content: center;
                justify-content: center;
                margin: 12px;
            }
        }

        .state-of-technology {
            color: white;
            padding-top: 24px;
            display: grid;
            grid-template-columns: 1fr 360px 360px 1fr;
            grid-gap: 16px;

            @media only screen and (max-width: $screen-sm-max) {
                grid-template-columns: 1fr 360px 1fr;
            }

            @media only screen and (max-width: $screen-xs-max) {
                grid-template-columns: 1fr 250px 1fr;
            }

            .sub-title {
                grid-column: 2 / span 2;

                @media only screen and (max-width: $screen-sm-max) {
                    grid-column: 2;
                }
            }

            .languages-used-chart {
                grid-column: 2 / span 2;

                @media only screen and (max-width: $screen-sm-max) {
                    grid-column: 2;
                }
            }

            .languages-used-vs-interest-chart {
                grid-column: 2 / span 2;

                @media only screen and (max-width: $screen-sm-max) {
                    grid-column: 2;
                }
            }

            .tool-usage-continued {
                grid-column: 2 / span 2;

                display: grid;

                grid-template-columns: 1fr 240px 120px 120px 240px 1fr;
                grid-template-rows: 350px 250px 200px 350px 75px 350px;
                grid-gap: 16px;

                grid-template-areas: '. ide    ide     issue   issue     .' '. text   text    text    text      .' '. sc     sc      shell   shell     .' '. area   area    dbs     dbs       .' '. fun    fun     fun     fun       .' '. deploy ci      ci      hosting   .';

                @media only screen and (max-width: $screen-sm-max) {
                    grid-column: 2;
                    grid-template-columns: 1fr 360px 1fr;
                    grid-template-rows: auto;

                    grid-template-areas: '. ide      .' '. issue    .' '. text     .' '. sc       .' '. shell    .' '. area     .' '. dbs      .' '. fun      .' '. deploy   .' '. ci       .' '. hosting  .';
                }

                @media only screen and (max-width: $screen-xs-max) {
                    grid-template-columns: 1fr 250px 1fr;
                }

                & > * {
                    align-self: center;
                    justify-self: center;
                }

                .ide {
                    grid-area: ide;
                }

                .text-editors {
                    grid-area: text;
                    width: 100%;
                }

                .issue-tracker {
                    grid-area: issue;
                }

                .source-control {
                    grid-area: sc;
                }

                .shell {
                    grid-area: shell;
                }

                .areas-of-work {
                    grid-area: area;
                }

                .databases {
                    grid-area: dbs;
                }

                .fun-facts {
                    width: 100%;
                    grid-area: fun;
                    display: grid;
                    grid-auto-flow: column;
                    align-items: start;
                    justify-items: center;
                    grid-template-columns: 1fr 1fr 1fr;

                    @media only screen and (max-width: $screen-sm-max) {
                        grid-template-columns: 1fr;
                        grid-auto-flow: row;
                        grid-gap: 32px;
                        margin-bottom: 32px;
                    }
                }

                .deployment-control {
                    grid-area: deploy;
                }

                .continuous-integration {
                    grid-area: ci;
                }

                .hosting-platform {
                    grid-area: hosting;
                }
            }
        }
    }

</style>
